/**
 * Named populate configurations per content type.
 *
 * Single-entry fetches (by slug) use custom Strapi endpoints that handle
 * deep populate server-side — no query params needed. These presets are
 * only used for lightweight collection queries (list, breadcrumb).
 */

export const POPULATE_PRESETS: Record<string, Record<string, Record<string, unknown>>> = {
  'asset-type-pages': {
    list: {
      hero: {
        populate: {
          backgroundImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
        },
      },
      cardImage: { fields: ['url', 'alternativeText', 'width', 'height', 'formats'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
    'archive-list': {
      hero: {
        populate: {
          backgroundImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
        },
      },
      cardImage: { fields: ['url', 'alternativeText', 'width', 'height', 'formats'] },
      parent: {
        fields: ['title', 'slug'],
      },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'industry-pages': {
    list: {
      hero: {
        populate: {
          backgroundImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
        },
      },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
    'archive-list': {
      hero: {
        populate: {
          backgroundImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
        },
      },
      assetTypes: {
        fields: ['title', 'slug'],
      },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'insight-pages': {
    list: {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
    'archive-list': {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      author: { fields: ['name', 'slug'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'news-pages': {
    list: {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
    'archive-list': {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      author: { fields: ['name', 'slug'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'case-studies': {
    list: {
      hero: {
        populate: {
          backgroundImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
        },
      },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
    'archive-list': {
      hero: {
        populate: {
          backgroundImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
        },
      },
      faultType: { fields: ['name', 'slug'] },
      assetTypes: { fields: ['title', 'slug'] },
      industries: { fields: ['title', 'slug'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'event-pages': {
    list: {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
    'archive-list': {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'partners': {
    'archive-list': {
      logo: { fields: ['url', 'width', 'height', 'alternativeText'] },
    },
  },
  'landing-pages': {
    // Note: no `full` preset — landing-pages uses the custom
    // /api/landing-pages/by-slug/:slug controller which owns populate
    // server-side (STATE decision 22-03 / 31-03). Only the `list` preset
    // remains for any future archive / preview query.
    list: {
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'lead-magnet-pages': {
    // Spec 34 (quick-70). Listing cards derive their thumbnail from the
    // first sections.hero-section's backgroundImage and their badge from
    // the hero overline (see buildArchiveItems). Spec lines ~63 warn that
    // ?populate=* on the listing returns shallow sections, so we populate
    // sections.hero-section deeply via Strapi v5's DZ `on` grammar. seo.metaImage
    // is populated for archive-card SEO parity with sibling CPTs.
    //
    // Detail pages do NOT use this preset — they go through the custom
    // /api/lead-magnet-pages/by-slug/:slug controller which handles deep
    // populate server-side (STATE decision 22-03 / 31-03).
    'archive-list': {
      sections: {
        on: {
          'sections.hero-section': {
            populate: {
              backgroundImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
            },
          },
        },
      },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'webinar-pages': {
    list: {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
      // Speakers — Pitfall 28: imageSrc populate explicitly omits `formats`
      // (200px card slot would alias formats.thumbnail; we want the parent .url).
      // `link` populate (spec 36 follow-up): items.card-item ships an optional
      // `link` component for the LinkedIn affordance — `populate: true` deep-
      // resolves the { href, text, style } shape.
      speakers: {
        populate: {
          imageSrc: { fields: ['url', 'alternativeText', 'width', 'height'] },
          link: true,
        },
      },
      // Related webinars — Pitfall 29: shallow populate would render imageless
      // cross-link cards; deep-populate featuredImage explicitly.
      relatedWebinars: {
        populate: {
          featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
        },
      },
    },
    'archive-list': {
      featuredImage: { fields: ['url', 'alternativeText', 'width', 'height'] },
      seo: {
        populate: {
          metaImage: { fields: ['url', 'width', 'height'] },
        },
      },
    },
  },
  'archive-pages': {
    breadcrumb: {},
  },
};

export function getPopulatePreset(contentType: string, preset: string): Record<string, unknown> {
  const presets = POPULATE_PRESETS[contentType];
  if (!presets) {
    throw new Error(`No populate presets for content type: ${contentType}`);
  }
  const config = presets[preset];
  if (!config) {
    throw new Error(`No preset "${preset}" for content type: ${contentType}`);
  }
  return config;
}
